import { useEffect, useRef } from 'react';
import { CloseButton, HStack, Icon, IconButton, Input, Text } from '@chakra-ui/react';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import { t } from '@lingui/core/macro';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';
import { CanvasFloatingBar } from '../CanvasFloatingBar';
import { testIds } from './testIds';

// The caller owns the match list and the viewport pan (see FlowEditor).
export function FindBar({
  query,
  matchCount,
  currentIndex,
  onQueryChange,
  onNext,
  onPrevious,
  onClose,
}: {
  query: string;
  matchCount: number;
  // Zero-based position of the current match, or -1 when there are none.
  currentIndex: number;
  onQueryChange: (query: string) => void;
  onNext: () => void;
  onPrevious: () => void;
  onClose: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const onInputKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (event.shiftKey) {
        onPrevious();
      } else {
        onNext();
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  const hasQuery = query.trim() !== '';
  const noMatches = hasQuery && matchCount === 0;

  return (
    <CanvasFloatingBar barTestId={testIds.bar} gripTestId={testIds.drag}>
      <HStack spacing={2}>
        <HStack spacing={1.5} pl={1} pr={1.5}>
          <Icon as={Search} boxSize={iconSize.sm} color="fg.muted" />
          <Input
            ref={inputRef}
            size="xs"
            variant="unstyled"
            w={pxToRem(160)}
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder={t`Find in conversation`}
            aria-label={t`Find in conversation`}
            data-testid={testIds.input}
          />
        </HStack>
        <Text
          fontSize="xs"
          color={noMatches ? 'fg.error' : 'fg.muted'}
          minW={pxToRem(52)}
          textAlign="right"
          sx={{ fontVariantNumeric: 'tabular-nums' }}
          data-testid={testIds.count}
        >
          {!hasQuery ? '' : noMatches ? t`No results` : `${currentIndex + 1} of ${matchCount}`}
        </Text>
        <HStack spacing={0.5}>
          <IconButton
            size="xs"
            variant="ghost"
            aria-label={t`Previous match`}
            icon={<Icon as={ChevronUp} boxSize={iconSize.sm} />}
            isDisabled={matchCount === 0}
            onClick={onPrevious}
            data-testid={testIds.previous}
          />
          <IconButton
            size="xs"
            variant="ghost"
            aria-label={t`Next match`}
            icon={<Icon as={ChevronDown} boxSize={iconSize.sm} />}
            isDisabled={matchCount === 0}
            onClick={onNext}
            data-testid={testIds.next}
          />
        </HStack>
        <CloseButton
          size="sm"
          onClick={onClose}
          aria-label={t`Close find`}
          data-testid={testIds.close}
        />
      </HStack>
    </CanvasFloatingBar>
  );
}
